import { z } from 'zod/v4'
import { CalculatedRole, createQueryOptionsSchema } from '../../shared/shared.types.js'
import type { AuthorizeClientOutput } from '../authorization/authorization.types.js'

const queryOptionsSchema = createQueryOptionsSchema(
	['name', 'deadline', 'status', 'cycleName', 'participantFirstName', 'formName'],
	'name',
)

export enum TaskStatusEnum {
	NOT_STARTED = 'not-started',
	IN_PROGRESS = 'in-progress',
	COMPLETED = 'completed',
	DECLINED = 'declined',
	REJECTED = 'rejected',
}

export const TERMINAL_TASK_STATUSES = [TaskStatusEnum.COMPLETED, TaskStatusEnum.DECLINED]

export enum HoldEditorEnum {
	SELF = 'self',
	MANAGER = 'mgr',
	CYCLE_ADMIN = 'ca',
}

export enum RaterListApproverEnum {
	MANAGER = 'manager',
	CYCLE_ADMIN = 'ca',
}

export const tasksSearchSchema = queryOptionsSchema.extend({
	search: z.string().optional(),
	cycleIds: z.array(z.string().uuid()).optional(),
	byStatus: z.array(z.nativeEnum(TaskStatusEnum)).optional(),
	excludeCalculatedRoles: z.array(z.nativeEnum(CalculatedRole)).optional(),
	includeCalculatedRoles: z.array(z.nativeEnum(CalculatedRole).nullable()).optional(),
	includeStale: z.boolean().optional().default(false),
	latestPerEvaluation: z.boolean().optional().default(false),
	onlyCurrentStep: z.boolean().optional().default(false),
})

export type TasksSearchInput = z.input<typeof tasksSearchSchema> & {
	userId?: string
	caUserId?: string
	excludeCompletedCycles?: boolean
}
export type ParsedTasksSearchInput = z.output<typeof tasksSearchSchema> & {
	userId?: string
	caUserId?: string
	excludeCompletedCycles?: boolean
}

// New schemas for form submission
export const formResponseSchema = z.object({
	componentId: z.string().uuid(),
	response: z.string(),
})

export const repeatableInstanceSchema = z.object({
	repeatableComponentId: z.string().uuid(), // The template repeatable container ID
	instanceCount: z.number().int().min(0), // Number of instances to create
})

export const submitFormSchema = z.object({
	taskId: z.string().uuid(),
	responses: z.array(formResponseSchema),
	repeatableInstances: z.array(repeatableInstanceSchema).optional(),
})

export const overwriteFormSchema = z.object({
	taskId: z.string().uuid(),
	responses: z.array(formResponseSchema),
})

export const saveFormSchema = z.object({
	taskId: z.string().uuid(),
	responses: z.array(
		z.object({
			componentId: z.string().uuid(),
			response: z.string(),
		}),
	),
})

// Schema for task completion
export const completeTaskSchema = z.object({
	taskId: z.string().uuid(),
	signatureTimestamp: z.date().optional(),
})

// Schema for task status update
export const updateTaskStatusSchema = z.object({
	taskId: z.string().uuid(),
	status: z.nativeEnum(TaskStatusEnum),
})

export const rejectTaskSchema = z.object({
	taskId: z.string().uuid(),
	signatureTimestamp: z.date().optional(),
	rejectionReason: z.string().optional(),
})

export const getTaskRejectionSchema = z.object({
	taskId: z.string().uuid(),
})

export const declineTaskSchema = z.object({
	taskId: z.string().uuid(),
	declineReason: z.string().optional(),
})

export const requestDictationSessionTokenSchema = z.object({
	scope: z.enum(['dictation']).default('dictation'),
})

export const rewriteDictatedTextSchema = z.object({
	text: z.string().min(1).max(10000),
	tone: z.enum(['concise', 'professional', 'empathetic']),
})

// Type exports
export type SubmitFormInput = z.infer<typeof submitFormSchema> & {
	userId: string
	permissions: AuthorizeClientOutput
}

export type OverwriteFormInput = z.infer<typeof overwriteFormSchema> & {
	userId: string
}

export type SaveFormInput = z.infer<typeof saveFormSchema> & {
	userId: string
	permissions: AuthorizeClientOutput
}

export type CompleteTaskInput = z.infer<typeof completeTaskSchema> & {
	userId: string
}

export type UpdateTaskStatusInput = z.infer<typeof updateTaskStatusSchema> & {
	userId: string
}

export type RejectTaskInput = z.infer<typeof rejectTaskSchema> & {
	userId: string
	permissions: AuthorizeClientOutput
}

export type GetTaskRejectionInput = z.infer<typeof getTaskRejectionSchema> & {
	userId: string
	organizationId: string
	permissions: AuthorizeClientOutput
}

export type DeclineTaskInput = z.infer<typeof declineTaskSchema> & {
	userId: string
}

export type RequestDictationSessionTokenInput = z.infer<typeof requestDictationSessionTokenSchema> & {
	userId: string
}

export type RewriteDictatedTextInput = z.infer<typeof rewriteDictatedTextSchema> & {
	userId: string
}

export type FormResponseInput = z.infer<typeof formResponseSchema>

// Schema for task summary by role
export const getTasksSummaryByRoleSchema = z.object({
	cycleIds: z.array(z.string().uuid()).optional(), // Optional cycle filtering
})

export type GetTasksSummaryByRoleInput = z.infer<typeof getTasksSummaryByRoleSchema> & {
	userId: string
}

export type TaskRoleSummary = {
	role: CalculatedRole | null
	total: number
	incomplete: number
	notStarted: number
	inProgress: number
	late: number
	completed: number
	declined: number
	rejected: number
	closestDeadline: string | null
	isOverdue: boolean
}
